/**
 * DealerAgreementSignModal — e-signature capture for the dealer warranty
 * agreement. Mirrors the commissioning sign-disclaimer flow but at the
 * dealer level.
 *
 * Flow: preview the unsigned PDF in an iframe → fill signer name / title
 * / email + choose typed-or-drawn signature → confirm "accepted" → submit.
 * On success, surfaces the signed-PDF download link.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";

export default function DealerAgreementSignModal({ customer, onClose, onSigned }) {
  const t = useT();
  const [status, setStatus] = useState(null);
  const [signerName, setSignerName] = useState("");
  const [signerTitle, setSignerTitle] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [signatureMethod, setSignatureMethod] = useState("typed");
  const [signatureData, setSignatureData] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signedResult, setSignedResult] = useState(null);
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);

  useEffect(() => {
    if (!customer?.customer_id) return;
    api.get(`/admin/dealers/${encodeURIComponent(customer.customer_id)}/agreement-status`)
      .then(r => setStatus(r.data))
      .catch(() => setStatus({ signed: false }));
  }, [customer?.customer_id]);

  const startDraw = (e) => {
    setDrawing(true);
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const rect = c.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo((e.clientX || e.touches?.[0]?.clientX || 0) - rect.left,
               (e.clientY || e.touches?.[0]?.clientY || 0) - rect.top);
  };
  const moveDraw = (e) => {
    if (!drawing) return;
    e.preventDefault?.();
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const rect = c.getBoundingClientRect();
    ctx.lineTo((e.clientX || e.touches?.[0]?.clientX || 0) - rect.left,
               (e.clientY || e.touches?.[0]?.clientY || 0) - rect.top);
    ctx.strokeStyle = "#1e40af";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.stroke();
  };
  const stopDraw = () => {
    setDrawing(false);
    const c = canvasRef.current;
    if (c) setSignatureData(c.toDataURL("image/png"));
  };
  const clearDraw = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setSignatureData("");
  };

  const submit = async () => {
    if (!signerName.trim()) {
      toast.error(t("dealer_sign.err_name_required"));
      return;
    }
    if (signatureMethod === "drawn" && !signatureData) {
      toast.error(t("dealer_sign.err_drawn_required"));
      return;
    }
    if (!accepted) {
      toast.error(t("dealer_sign.err_accept_required"));
      return;
    }
    setSigning(true);
    try {
      const r = await api.post(
        `/admin/dealers/${encodeURIComponent(customer.customer_id)}/sign-agreement`,
        {
          signer_name: signerName.trim(),
          signer_title: signerTitle.trim(),
          signer_email: signerEmail.trim(),
          signature_method: signatureMethod,
          signature_data: signatureMethod === "drawn" ? signatureData : "",
          accepted: true,
        },
      );
      setSignedResult(r.data);
      toast.success(t("dealer_sign.success"));
      onSigned?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign failed");
    } finally {
      setSigning(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-md shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        data-testid="dealer-sign-modal">
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold">{t("dealer_sign.modal_title")}</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {customer?.company_name || customer?.customer_id}
              {status?.signed && (
                <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300">
                  {t("dealer_sign.signed_previously")}
                </span>
              )}
            </p>
          </div>
          <button data-testid="dealer-sign-close" onClick={onClose}
            className="text-slate-500 hover:text-slate-800 text-xl leading-none">×</button>
        </div>

        <div className="p-5 space-y-4">
          {status?.signed && status?.signature && !signedResult && (
            <div className="border-l-4 border-emerald-400 bg-emerald-50 rounded p-3 text-sm">
              <div className="font-bold text-emerald-900">{t("dealer_sign.already_signed_title")}</div>
              <div className="text-xs text-slate-700 mt-1">
                {t("dealer_sign.signed_meta", null, {
                  name: status.signature.signer_name || "—",
                  at: (status.signature.signed_at || "").slice(0, 19).replace("T", " "),
                  version: status.signature.master_doc_version || "—",
                })}
              </div>
              <a data-testid="dealer-sign-view-previous"
                href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/dealers/${encodeURIComponent(customer.customer_id)}/signed-agreement.pdf`}
                target="_blank" rel="noopener noreferrer"
                className="inline-block mt-2 text-xs font-bold text-blue-700 hover:text-blue-900">
                {t("dealer_sign.view_existing_pdf")} →
              </a>
            </div>
          )}

          {signedResult ? (
            <div data-testid="dealer-sign-success"
              className="border-2 border-green-400 bg-green-50 rounded p-4">
              <h4 className="text-lg font-bold text-green-800">{t("dealer_sign.success_headline")}</h4>
              <p className="text-sm mt-1">
                {t("dealer_sign.success_body", null, {
                  to_count: signedResult.email_recipients?.to?.length || 0,
                  cc_count: signedResult.email_recipients?.cc?.length || 0,
                })}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <a data-testid="dealer-sign-pdf-link"
                  href={`${process.env.REACT_APP_BACKEND_URL}${signedResult.signed_pdf_url}`}
                  target="_blank" rel="noopener noreferrer"
                  className="px-3 py-2 bg-blue-600 text-white rounded text-sm font-bold hover:bg-blue-700">
                  {t("dealer_sign.download_signed_pdf")}
                </a>
                <button onClick={onClose} className="px-3 py-2 border rounded text-sm">
                  {t("aux.cancel")}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="border rounded overflow-hidden" style={{ height: "360px" }}>
                <iframe data-testid="dealer-sign-preview-iframe"
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/admin/dealers/${encodeURIComponent(customer.customer_id)}/agreement.pdf`}
                  title="Dealer agreement preview"
                  className="w-full h-full" />
              </div>
              <p className="text-[11px] text-slate-500 italic">{t("dealer_sign.preview_hint")}</p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold text-slate-600 block mb-1">
                    {t("dealer_sign.signer_name")}
                  </label>
                  <input data-testid="dealer-sign-signer-name"
                    value={signerName} onChange={e => setSignerName(e.target.value)}
                    placeholder={t("dealer_sign.signer_name_placeholder")}
                    className="w-full border rounded px-2 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold text-slate-600 block mb-1">
                    {t("dealer_sign.signer_title")}
                  </label>
                  <input data-testid="dealer-sign-signer-title"
                    value={signerTitle} onChange={e => setSignerTitle(e.target.value)}
                    placeholder={t("dealer_sign.signer_title_placeholder")}
                    className="w-full border rounded px-2 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold text-slate-600 block mb-1">
                    {t("dealer_sign.signer_email")}
                  </label>
                  <input data-testid="dealer-sign-signer-email" type="email"
                    value={signerEmail} onChange={e => setSignerEmail(e.target.value)}
                    placeholder={t("dealer_sign.signer_email_placeholder")}
                    className="w-full border rounded px-2 py-1.5 text-sm" />
                </div>
              </div>

              <div>
                <div className="flex gap-2 mb-2 text-sm">
                  <button data-testid="dealer-sign-method-typed"
                    onClick={() => setSignatureMethod("typed")}
                    className={`px-3 py-1 rounded border ${signatureMethod === "typed"
                      ? "bg-blue-600 text-white border-blue-600"
                      : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
                    {t("dealer_sign.method_typed")}
                  </button>
                  <button data-testid="dealer-sign-method-drawn"
                    onClick={() => setSignatureMethod("drawn")}
                    className={`px-3 py-1 rounded border ${signatureMethod === "drawn"
                      ? "bg-blue-600 text-white border-blue-600"
                      : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
                    {t("dealer_sign.method_drawn")}
                  </button>
                </div>
                {signatureMethod === "typed" ? (
                  <div className="border rounded px-3 py-2 bg-slate-50">
                    <p className="text-xs text-slate-500 mb-1">{t("dealer_sign.typed_hint")}</p>
                    <div className="font-['Chivo'] italic text-blue-800 text-2xl tracking-wide min-h-[2.5rem]">
                      {signerName || <span className="text-slate-400">—</span>}
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="border rounded bg-slate-50">
                      <canvas data-testid="dealer-sign-canvas"
                        ref={canvasRef} width={600} height={140}
                        onMouseDown={startDraw} onMouseMove={moveDraw}
                        onMouseUp={stopDraw} onMouseLeave={stopDraw}
                        onTouchStart={startDraw} onTouchMove={moveDraw}
                        onTouchEnd={stopDraw}
                        className="w-full touch-none cursor-crosshair" />
                    </div>
                    <button data-testid="dealer-sign-clear-canvas"
                      onClick={clearDraw}
                      className="text-xs text-slate-600 hover:text-slate-900 mt-1">
                      {t("dealer_sign.clear_drawing")}
                    </button>
                  </div>
                )}
              </div>

              <label className="flex items-start gap-2 text-sm border rounded p-3 bg-amber-50 border-amber-200">
                <input data-testid="dealer-sign-accept"
                  type="checkbox" checked={accepted}
                  onChange={e => setAccepted(e.target.checked)}
                  className="mt-0.5" />
                <span>{t("dealer_sign.accept_clause")}</span>
              </label>

              <div className="flex justify-end gap-2">
                <button data-testid="dealer-sign-cancel"
                  onClick={onClose} className="px-3 py-2 text-sm border rounded">
                  {t("aux.cancel")}
                </button>
                <button data-testid="dealer-sign-submit"
                  onClick={submit} disabled={signing}
                  className="px-4 py-2 bg-blue-600 text-white rounded font-bold text-sm hover:bg-blue-700 disabled:opacity-50">
                  {signing ? t("dealer_sign.signing") : t("dealer_sign.submit")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
